import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMemo, useState } from "react";
import { useFinance } from "./FinanceContext";
import { MONTHS } from "./data/seedData";
import { StatusPill } from "./components/StatusChip";
import { toneColor } from "./components/tone";

export function VersionsPage() {
  const { versions, fmt } = useFinance();
  const withSnap = useMemo(() => versions.filter((v) => v.snapshot), [versions]);
  const [a, setA] = useState(withSnap[0]?.id ?? "");
  const [b, setB] = useState(withSnap[withSnap.length - 1]?.id ?? "");
  const va = versions.find((v) => v.id === a);
  const vb = versions.find((v) => v.id === b);

  const diffs = useMemo(() => {
    if (!va || !vb || !va.snapshot || !vb.snapshot) return [];
    const A: Record<string, (typeof va.snapshot.items)[number]> = {};
    va.snapshot.items.forEach((it) => (A[it.led] = it));
    const out: {
      code: string;
      name: string;
      field: string;
      month: string;
      old: number;
      new: number;
    }[] = [];
    vb.snapshot.items.forEach((it) => {
      const old = A[it.led];
      for (let m = 0; m < 12; m++) {
        (["planned", "actual"] as const).forEach((f) => {
          const ov = old ? (old[f][m] ?? 0) : 0;
          const nv = it[f][m] ?? 0;
          if (Math.round(ov) !== Math.round(nv)) {
            out.push({
              code: it.led,
              name: it.name,
              field: f === "planned" ? "Budget" : "Actual",
              month: MONTHS[m] ?? "",
              old: ov,
              new: nv,
            });
          }
        });
      }
    });
    return out;
  }, [va, vb]);

  const budgetVers = versions.filter((v) => v.kind === "Budget");

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Version History &amp; Comparison
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Budget-plan versions · only one version is Active (official) · compare any two cell-by-cell
      </Typography>

      <Box sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
          Budget Version History
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Version</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Parent</TableCell>
                <TableCell>By</TableCell>
                <TableCell>Approved</TableCell>
                <TableCell>Frozen</TableCell>
                <TableCell>Published</TableCell>
                <TableCell>Active</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {budgetVers.length ? (
                budgetVers.map((v) => (
                  <TableRow key={v.id}>
                    <TableCell sx={{ fontWeight: 700 }}>{v.ver}</TableCell>
                    <TableCell>
                      <StatusPill status={v.status} />
                    </TableCell>
                    <TableCell>{v.reason || v.label}</TableCell>
                    <TableCell>{v.parentVersion || "—"}</TableCell>
                    <TableCell>{v.uploadedBy}</TableCell>
                    <TableCell>{v.approvedDate || "—"}</TableCell>
                    <TableCell>{v.freezeDate || "—"}</TableCell>
                    <TableCell>{v.publishedDate || "—"}</TableCell>
                    <TableCell>
                      {v.active ? (
                        <StatusPill status="Active" />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Archived
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9}>No budget versions yet.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Every approved budget version is retained; only the latest Published version is Active and
          drives the Dashboard, Budget vs Actual &amp; Reports.
        </Typography>
      </Box>

      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          Compare
        </Typography>
        <TextField
          select
          size="small"
          value={a}
          onChange={(e) => setA(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          {withSnap.map((v) => (
            <MenuItem key={v.id} value={v.id}>
              {v.ver} — {v.label}
            </MenuItem>
          ))}
        </TextField>
        <Typography variant="body2" color="text.secondary">
          vs
        </Typography>
        <TextField
          select
          size="small"
          value={b}
          onChange={(e) => setB(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          {withSnap.map((v) => (
            <MenuItem key={v.id} value={v.id}>
              {v.ver} — {v.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      {vb?.reason ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Change reason for {vb.ver}: <strong>{vb.reason}</strong> · by {vb.uploadedBy} ·{" "}
          {vb.publishedDate || vb.uploadDate}
        </Alert>
      ) : null}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Code</TableCell>
              <TableCell>Cost Head</TableCell>
              <TableCell>Field</TableCell>
              <TableCell>Month</TableCell>
              <TableCell align="right">{va ? va.ver : "A"} (Old)</TableCell>
              <TableCell align="right">{vb ? vb.ver : "B"} (New)</TableCell>
              <TableCell align="right">Difference</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {diffs.length ? (
              diffs.map((d, i) => (
                <TableRow key={i}>
                  <TableCell>{d.code}</TableCell>
                  <TableCell>{d.name}</TableCell>
                  <TableCell>{d.field}</TableCell>
                  <TableCell>{d.month}</TableCell>
                  <TableCell align="right">{fmt(d.old)}</TableCell>
                  <TableCell align="right" sx={{ bgcolor: "action.hover" }}>
                    {fmt(d.new)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: toneColor(d.new - d.old >= 0 ? "pos" : "neg") }}
                  >
                    {(d.new - d.old >= 0 ? "+" : "") + fmt(d.new - d.old)}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  No differences between these versions{a === b ? " (same version selected)." : "."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        {diffs.length} changed cell(s). Older versions are never overwritten — snapshots retained
        for full traceability.
      </Typography>
    </Box>
  );
}
